import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom-v5-compat';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { FlexColumn } from 'ui-components/components/flex';

import { SupplierPreview } from './preview/supplier-preview';
import { SupplierBreadcrumb } from './supplier-breadcrumb';
import { SupplierHeader } from './supplier-header';
import style from './supplier-page.module.less';
import { SupplierModal } from '../../purchase-orders/purchase-order-page/edit/supplier-modal/supplier-modal';
import { useSupplierModal } from '../hooks/use-supplier-modal';
import { useSuppliersGridContext } from '../suppliers-grid-context';
import type { SupplierProduct } from '../types/supplier-product-types';
import { PagingContextProvider } from '../../../../contexts/paging-context';
import { SupplierProductsGridContextProvider } from './supplier-products-grid-context';
import { useUserSetting } from '../../../../hooks/use-user-setting';
import { UserSettings } from '../../../../enums/user-settings/user-settings';
import { PAGE_SIZE_50 } from '../../../../services/pagination-service';

const SupplierDetailPageContent = () => {
  const { supplierId } = useParams<{ supplierId: string }>();
  const queryClient = useQueryClient();
  const { isLoading, refreshSuppliers, getSupplierById } = useSuppliersGridContext();
  const { isSupplierModalOpen, selectedSupplier, isEditMode, closeSupplierModal, openEditSupplierModal } = useSupplierModal();
  const [selectedProducts, setSelectedProducts] = useState<SupplierProduct[]>([]);

  const { data: supplier = null, isLoading: isLoadingSupplier } = useQuery({
    queryKey: ['supplier', supplierId],
    queryFn: () => getSupplierById(supplierId!),
    enabled: !!supplierId,
  });

  const handleSupplierSaved = useCallback(async () => {
    await refreshSuppliers();
    await queryClient.invalidateQueries({ queryKey: ['supplier', supplierId] });
  }, [queryClient, refreshSuppliers, supplierId]);

  return (
    <>
      {isLoading || isLoadingSupplier ? (
        <LoadingIndicator />
      ) : (
        <FlexColumn className={style.container}>
          <SupplierBreadcrumb supplier={supplier} />
          <FlexColumn className={style.content}>
            <SupplierHeader onEditSupplier={openEditSupplierModal} selectedProducts={selectedProducts} supplier={supplier} />
            <SupplierPreview onSelectedProductsChanged={setSelectedProducts} supplier={supplier} />
          </FlexColumn>
        </FlexColumn>
      )}
      {isSupplierModalOpen ? (
        <SupplierModal
          isEditing={isEditMode}
          onClose={closeSupplierModal}
          onSupplierSaved={handleSupplierSaved}
          supplier={selectedSupplier}
        />
      ) : null}
    </>
  );
};

export const SupplierDetailPage = () => {
  const { supplierId } = useParams<{ supplierId: string }>();
  const userSetting = useUserSetting(UserSettings.supplierProductsGridPageSize.settingName);
  const userPageSize = useMemo(() => parseInt(userSetting || PAGE_SIZE_50, 10), [userSetting]);

  return (
    <PagingContextProvider page={1} pageSize={userPageSize}>
      <SupplierProductsGridContextProvider supplierId={Number(supplierId!)}>
        <SupplierDetailPageContent />
      </SupplierProductsGridContextProvider>
    </PagingContextProvider>
  );
};
